import React, { useEffect, useRef } from "react";
import * as THREE from "three";

type Props = {
  src: string; // model url
  type: "obj" | "stl" | "fbx"; // loader type
  height?: number;
};

// Lightweight viewer: STL via STLLoader, OBJ via OBJLoader; FBX optional (lazy)
const ModelViewer: React.FC<Props> = ({ src, type, height = 320 }) => {
  const mountRef = useRef<HTMLDivElement | null>(null);
  const rendererRef = useRef<any>(null);
  const sceneRef = useRef<any>(null);
  const cameraRef = useRef<any>(null);
  const animationIdRef = useRef<number | null>(null);
  const controlsRef = useRef<any>(null);

  useEffect(() => {
    const mount = mountRef.current!;

    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0xf5f5f5);
    sceneRef.current = scene;

    const width = mount.clientWidth;
    const camera = new THREE.PerspectiveCamera(45, width / height, 0.1, 1000);
    camera.position.set(0, 1, 4);
    cameraRef.current = camera;

    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setSize(width, height);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    mount.appendChild(renderer.domElement);
    rendererRef.current = renderer;

    // Lights
    const ambient = new THREE.AmbientLight(0xffffff, 1);
    scene.add(ambient);
    const dir = new THREE.DirectionalLight(0xffffff, 0.8);
    dir.position.set(3, 5, 2);
    scene.add(dir);

    // Ground
    const ground = new THREE.Mesh(
      new THREE.PlaneGeometry(10, 10),
      new THREE.MeshPhongMaterial({ color: 0xffffff })
    );
    ground.rotation.x = -Math.PI / 2;
    ground.position.y = -0.8;
    ground.receiveShadow = true;
    scene.add(ground);

    let model: any | null = null;

    const setupControls = async () => {
      const { OrbitControls } = await import(
        "three/examples/jsm/controls/OrbitControls.js"
      );
      const controls = new OrbitControls(camera, renderer.domElement);
      controls.enableDamping = true;
      controls.dampingFactor = 0.05;
      controls.enablePan = false;
      controls.minDistance = 0.4;
      controls.maxDistance = 20;
      controlsRef.current = controls;
    };
    setupControls();

    const loadModel = async () => {
      if (type === "stl") {
        const { STLLoader } = await import(
          "three/examples/jsm/loaders/STLLoader.js"
        );
        const loader = new STLLoader();
        loader.load(src, (geometry: any) => {
          const material = new THREE.MeshPhongMaterial({
            color: 0xbdbdbd,
            specular: 0x111111,
            shininess: 50,
          });
          const mesh = new THREE.Mesh(geometry, material);
          mesh.rotation.x = -Math.PI / 2;
          scene.add(mesh);
          model = mesh;
          fitCameraToObject(camera, mesh, 1.2);
          centerControlsOnObject(mesh);
        });
      } else if (type === "obj") {
        const { OBJLoader } = await import(
          "three/examples/jsm/loaders/OBJLoader.js"
        );
        const loader = new OBJLoader();
        loader.load(src, (obj: any) => {
          scene.add(obj);
          model = obj;
          fitCameraToObject(camera, obj, 1.2);
          centerControlsOnObject(obj);
        });
      } else if (type === "fbx") {
        const { FBXLoader } = await import(
          "three/examples/jsm/loaders/FBXLoader.js"
        );
        const loader = new FBXLoader();
        loader.load(src, (obj: any) => {
          scene.add(obj);
          model = obj;
          fitCameraToObject(camera, obj, 1.4);
          centerControlsOnObject(obj);
        });
      }
    };

    const fitCameraToObject = (camera: any, object: any, offset: number = 1.25) => {
      const box = new THREE.Box3().setFromObject(object);
      const size = box.getSize(new THREE.Vector3());
      const center = box.getCenter(new THREE.Vector3());

      const maxDim = Math.max(size.x, size.y, size.z);
      const fov = camera.fov * (Math.PI / 180);
      let cameraZ = Math.abs(maxDim / 2 / Math.tan(fov / 2)) * offset;
      camera.position.set(center.x, center.y + maxDim * 0.2, cameraZ);
      camera.lookAt(center);
    };

    const centerControlsOnObject = (object: any) => {
      if (!controlsRef.current) return;
      const box = new THREE.Box3().setFromObject(object);
      const center = box.getCenter(new THREE.Vector3());
      controlsRef.current.target.copy(center);
      controlsRef.current.update();
    };

    loadModel();

    const onResize = () => {
      const newWidth = mount.clientWidth;
      renderer.setSize(newWidth, height);
      camera.aspect = newWidth / height;
      camera.updateProjectionMatrix();
    };
    window.addEventListener("resize", onResize);

    const animate = () => {
      animationIdRef.current = requestAnimationFrame(animate);
      if (model) {
        model.rotation.y += 0.003;
      }
      controlsRef.current?.update();
      renderer.render(scene, camera);
    };
    animate();

    return () => {
      if (animationIdRef.current) cancelAnimationFrame(animationIdRef.current);
      window.removeEventListener("resize", onResize);
      if (rendererRef.current) {
        mount.removeChild(rendererRef.current.domElement);
        rendererRef.current.dispose();
      }
      controlsRef.current?.dispose?.();
    };
  }, [src, type, height]);

  return <div ref={mountRef} style={{ width: "100%", height }} />;
};

export default ModelViewer;
